import * as Phaser from 'phaser';

export const FROSTMERE_RESTORATION_KEY = 'restoration:frostmere_expanse';
const CROSSFADE_MS = 1200;

export type RestorationTier = 0 | 50 | 100;

export function getRestorationTier(pct: number): RestorationTier {
  if (pct >= 100) return 100;
  if (pct >= 50) return 50;
  return 0;
}

export function readFrostmereRestoration(game: Phaser.Game): number {
  return (game.registry.get(FROSTMERE_RESTORATION_KEY) as number) ?? 0;
}

export interface VariantSwitcher {
  currentTier: RestorationTier;
  destroy: () => void;
}

interface VariantConfig {
  tiers: RestorationTier[];
  layerPaths: Partial<Record<RestorationTier, Record<string, string>>>;
}

const LAYER_IDS = ['sky', 'far-bg', 'mid', 'fg'] as const;

export function preloadVariantLayers(
  scene: Phaser.Scene,
  sceneId: string,
  config: VariantConfig,
): void {
  for (const tier of config.tiers) {
    const paths = config.layerPaths[tier];
    if (!paths) continue;
    for (const layerId of LAYER_IDS) {
      const src = paths[layerId];
      if (!src) continue;
      const texKey = `${sceneId}:variant-${tier}:${layerId}`;
      if (!scene.textures.exists(texKey)) {
        scene.load.image(texKey, src);
      }
    }
  }
}

function clampTierToAvailable(tier: RestorationTier, tiers: RestorationTier[]): RestorationTier {
  if (tiers.includes(tier)) return tier;
  const sorted = [...tiers].sort((a, b) => b - a);
  for (const t of sorted) {
    if (t <= tier) return t;
  }
  return sorted[sorted.length - 1];
}

export function buildVariantSwitcher(
  scene: Phaser.Scene,
  sceneId: string,
  config: VariantConfig,
): VariantSwitcher {
  const w = scene.scale.width;
  const h = scene.scale.height;
  const pct = readFrostmereRestoration(scene.game);
  const initialTier = clampTierToAvailable(getRestorationTier(pct), config.tiers);

  const variantSets: Map<RestorationTier, Phaser.GameObjects.Image[]> = new Map();

  for (const tier of config.tiers) {
    const images: Phaser.GameObjects.Image[] = [];
    for (const layerId of LAYER_IDS) {
      const texKey = `${sceneId}:variant-${tier}:${layerId}`;
      if (!scene.textures.exists(texKey)) continue;
      const img = scene.add.image(w / 2, h / 2, texKey);
      img.setOrigin(0.5, 0.5);
      const depthMap: Record<string, number> = { sky: 0.5, 'far-bg': 1.5, mid: 3.5, fg: 5.5 };
      img.setDepth(depthMap[layerId] ?? 3.5);
      const scaleX = w / img.width;
      const scaleY = h / img.height;
      img.setScale(Math.max(scaleX, scaleY));
      img.setAlpha(tier === initialTier ? 1 : 0);
      img.setScrollFactor(0);
      images.push(img);
    }
    variantSets.set(tier, images);
  }

  let currentTier = initialTier;

  const onRegistryChange = (_parent: unknown, value: number) => {
    const newTier = clampTierToAvailable(getRestorationTier(value), config.tiers);
    if (newTier === currentTier) return;
    const oldImages = variantSets.get(currentTier);
    const newImages = variantSets.get(newTier);
    if (oldImages) {
      for (const img of oldImages) {
        scene.tweens.add({ targets: img, alpha: 0, duration: CROSSFADE_MS, ease: 'Sine.easeInOut' });
      }
    }
    if (newImages) {
      for (const img of newImages) {
        scene.tweens.add({ targets: img, alpha: 1, duration: CROSSFADE_MS, ease: 'Sine.easeInOut' });
      }
    }
    currentTier = newTier;
  };

  scene.game.registry.events.on(`changedata-${FROSTMERE_RESTORATION_KEY}`, onRegistryChange);

  return {
    get currentTier() { return currentTier; },
    destroy() {
      scene.game.registry.events.off(`changedata-${FROSTMERE_RESTORATION_KEY}`, onRegistryChange);
      for (const images of variantSets.values()) {
        for (const img of images) img.destroy();
      }
      variantSets.clear();
    },
  };
}
